import { useCallback, useMemo, useState } from 'react';

import { GeographyHierarchy } from 'hooks/data/search/types';

import { buildEffectiveCoords, flattenHierarchy, selectAllChildren } from './utils';

const useSpecialGeographies = (geographyHierarchy: GeographyHierarchy | undefined, appliedGeographies: string[]) => {
  const [selectedSpecialGeographies, setSelectedSpecialGeographies] = useState<Record<string, string>>({});

  const specialHierarchy = useMemo(
    () => geographyHierarchy?.['Special Geographies']?.children ?? undefined,
    [geographyHierarchy]
  );

  // Stored separately from World selection state, so numeric coordinates won't collide.
  const allFlatSpecialGeographies = useMemo(
    () => (specialHierarchy ? flattenHierarchy(specialHierarchy, undefined, [], true) : []),
    [specialHierarchy]
  );

  const effectivelySelectedSpecialCoords = useMemo(
    () => buildEffectiveCoords(selectedSpecialGeographies, allFlatSpecialGeographies),
    [selectedSpecialGeographies, allFlatSpecialGeographies]
  );

  const buildSpecialSelectionFromApplied = useCallback(() => {
    const newSelected: Record<string, string> = {};
    for (const name of appliedGeographies) {
      const item =
        allFlatSpecialGeographies.find((f) => f.name === name && !f.hasChildren) ??
        allFlatSpecialGeographies.find((f) => f.name === name);
      if (item) newSelected[item.coordinates] = item.name;
    }
    return newSelected;
  }, [appliedGeographies, allFlatSpecialGeographies]);

  const handleSpecialSelect = useCallback(
    (geographyName: string, coordinates: string) => {
      if (selectedSpecialGeographies[coordinates]) {
        setSelectedSpecialGeographies((prev) =>
          Object.fromEntries(Object.entries(prev).filter(([key]) => key !== coordinates && !key.startsWith(coordinates + '.')))
        );
      } else {
        setSelectedSpecialGeographies((prev) => {
          const clickedItem = allFlatSpecialGeographies.find((f) => f.coordinates === coordinates);
          const childrenSelections =
            clickedItem?.hasChildren && specialHierarchy ? selectAllChildren(specialHierarchy, coordinates, geographyName) : {};
          return { ...prev, [coordinates]: geographyName, ...childrenSelections };
        });
      }
    },
    [selectedSpecialGeographies, allFlatSpecialGeographies, specialHierarchy]
  );

  const selectedSpecialFiltered = useMemo(
    () => [...new Set(Object.values(selectedSpecialGeographies))],
    [selectedSpecialGeographies]
  );

  return {
    specialHierarchy,
    allFlatSpecialGeographies,
    selectedSpecialGeographies,
    setSelectedSpecialGeographies,
    effectivelySelectedSpecialCoords,
    buildSpecialSelectionFromApplied,
    handleSpecialSelect,
    selectedSpecialFiltered,
  };
};

export default useSpecialGeographies;
